// Review marks behind the `FileReviewPort` GitHub's per-file "Viewed" sync
// uses (viewedSync.svelte.ts): whether a file is fully reviewed at the pull
// request's head, and marking or unmarking a whole file. Read on a
// base…head model at the head, which is what Viewed mirrors
// (docs/design.md "Review marks", GitHub Viewed): `fileReviewed` says it,
// and `planFile` writes it, as anchors on the head blob for the added
// lines and on the base blob for the removed ones, through the normal mark
// write (MarksStore), so it reaches the server and every device, and
// after a push the file shows reviewed again exactly where its lines did
// not change.
import { sameCommit } from '../logic/snapshotLink';
import { fileReviewed } from '../review/model';
import type { DiffModel } from './diffs.svelte';
import type { MarksStore } from './marks.svelte';
import type { FileReviewPort } from './viewedSync.svelte';

export type { FileReviewPort };

/**
 * Whether every changed line of `path` is reviewed (`fileReviewed` on the
 * model's review state); null while the file's state is unknown (not
 * diffed, marks of it still carrying or resolving, the engine
 * unavailable: `DiffModel.reviewKnown`), and for a file with no changed
 * line (only a rename or a mode change: nothing to review, so nothing to
 * say either way).
 */
export function fullyReviewed(model: DiffModel, path: string): boolean | null {
  const f = model.fileReview(path);
  if (!f || f.status === 'unknown' || f.status === 'empty') return null;
  // Marks still on their way (a line map, a key-based mark not looked for
  // yet) or the engine unavailable: "not reviewed" is not known, and the
  // sync must not clear Viewed on GitHub from it.
  if (f.waiting.length || !model.reviewKnown(path)) return null;
  return fileReviewed(model.review, path);
}

/**
 * An identity of a file's diff: its base and head blobs, or (base…head
 * lists only the head blob) the head blob and a hash of GitHub's patch,
 * which differs when the base side does; null when neither is known.
 */
export function diffIdentity(oldSha: string | null, newSha: string | null, patch: string | null): string | null {
  if (oldSha) return `${oldSha}..${newSha ?? ''}`;
  if (patch === null) return null;
  // FNV-1a over the patch, with its length.
  let h = 0x811c9dc5;
  for (let i = 0; i < patch.length; i++) h = Math.imul(h ^ patch.charCodeAt(i), 0x01000193);
  return `${newSha ?? ''}#${patch.length}.${(h >>> 0).toString(16)}`;
}

/**
 * The port for `model`, which must show base…head at `head` (it refuses
 * any other: a model built before a push would mark the old head's
 * blobs). Marking refuses (throws) when asked for another head, or once
 * the model moved off `head`, before or while the file's diff loads: a
 * decision made at one head never marks another's changes.
 */
export function diffModelReview(model: DiffModel, marks: MarksStore, head: string): FileReviewPort {
  if (model.interdiff) throw new Error('the Viewed sync reads base…head, not an interdiff');
  if (!sameCommit(model.head, head)) throw new Error(`the model shows head ${model.head?.slice(0, 7)}, not ${head.slice(0, 7)}`);
  const atHead = (asked: string) => {
    if (asked !== head) throw new Error(`asked to mark at ${asked.slice(0, 7)}, judged at ${head.slice(0, 7)}`);
    if (!sameCommit(model.head, head) || model.interdiff) throw new Error('the pull request moved');
  };
  return {
    head,
    fullyReviewed: (path) => fullyReviewed(model, path),
    diff: (path) => {
      const e = model.entries.find((x) => x.path === path);
      return e ? diffIdentity(e.oldSha, e.newSha, e.patch) : null;
    },
    async setFile(path, reviewed, asked) {
      atHead(asked);
      const d = await model.settled(path);
      atHead(asked);
      if (!d.set) throw new Error(d.failure?.message ?? 'its diff could not be computed');
      // An unmark must find the anchors of key-based marks shown here.
      if (!reviewed && model.unrecorded) {
        if (!(await model.recordResolved())) throw new Error('earlier review marks could not be recorded yet');
        model.setMarks(marks.anchors);
        model.setLegacy(marks.legacy);
        atHead(asked);
      }
      const plan = model.planFile(path, reviewed);
      if (!plan) throw new Error('its changes are not computed');
      marks.apply(plan);
      model.setMarks(marks.anchors);
    },
  };
}
